/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, no-restricted-properties */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { setDiff, alias } from '@ember/object/computed';
import { get, action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import MedicalConditionType from 'linced-app/enums/medical-condition-type';
import NSWStatus from '@linc-technologies/linced-common/enums/nsw-status';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { matchYearLevel } from 'linced-app/utils/year-levels';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import {
  filterBy,
  findBy,
  mapBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';

export default class MedicalRoomController extends Controller {
  @action
  setFullNameFilter(value) {
    this.fullNameFilter = value;
  }

  @action
  setIncludeNonCurrentConditions(value) {
    this.includeNonCurrentConditions = value;
  }

  @action
  setIncludeNonCurrentMedical(value) {
    this.includeNonCurrentMedical = value;
  }

  @action
  setIncludeStudentsWithNoMedical(value) {
    this.includeStudentsWithNoMedical = value;
  }

  @action
  setPlanFilter(value) {
    this.planFilter = value;
  }

  matchYearLevel = matchYearLevel;

  @service
  session;

  @service
  strings;

  title = 'Medical Room';
  @tracked includeNonCurrentMedical = false;
  @tracked includeNonCurrentConditions = false;
  @tracked includeStudentsWithNoMedical = true;

  @alias('session.school')
  school;

  @tracked groupFilter = [];
  @tracked yearLevelFilter = [];
  @tracked riskFilter = [];
  @tracked selected = [];
  @tracked fullNameFilter = '';
  @tracked planFilter = '';
  riskOptions = Object.freeze(['Anaphylaxis', 'High Risk']);

  @setDiff('riskOptions', 'riskFilter')
  remainingRisks;

  @computed('riskFilter.[]')
  get hasAnaphylaxis() {
    return this.riskFilter.includes('Anaphylaxis');
  }

  @setDiff('school.yearLevels', 'yearLevelFilter')
  remainingYearLevels;

  // groupMembershipMap is a native getter rather than a computed.
  // `currentLearners` is itself a native getter on the group model, so it can
  // never invalidate an @each dependent key. Iterating here consumes the
  // underlying `learners` tags via autotracking.
  get groupMembershipMap() {
    let membership = Object.create(null);
    (this.model.groups || []).forEach((group) => {
      let learners = get(group, 'currentLearners') ?? [];
      learners.forEach((learner) => {
        let personId = get(learner, 'personId') ?? learner.personId;
        if (isEmpty(personId)) {
          return;
        }
        if (isEmpty(membership[personId])) {
          membership[personId] = [];
        }
        membership[personId].push(group);
      });
    });
    return membership;
  }

  @computed(
    'model.learners.@each.{fullName,firstName,lastName,currentYearLevel,numericCurrentYearLevel,image,homeroom,medicalConditions,medicalContacts,currentMedicalContacts}',
    'model.medications.@each.{deleted,medicalCondition,person_id}',
    'includeNonCurrentConditions',
    'includeNonCurrentMedical',
    'includeStudentsWithNoMedical',
    'hasAnaphylaxis',
    'groupMembershipMap',
  )
  get learnerFilterMap() {
    let learners = this.model.learners || [];
    return learners.map((person) => this._buildLearnerRow(person));
  }

  @computed(
    'learnerFilterMap.[]',
    'learnerFilterMap.@each.{groupIds,yearLevel,risks}',
    'yearLevelFilter.[]',
    'groupFilter.[]',
    'riskFilter.[]',
    'includeStudentsWithNoMedical',
  )
  get filteredLearners() {
    let rows = this.learnerFilterMap;
    let years = this.yearLevelFilter;
    let groups = this.groupFilter;
    let risks = this.riskFilter;
    let hasGroups = !isEmpty(groups);
    let hasYears = !isEmpty(years);
    let hasRisks = !isEmpty(risks);

    return rows.filter((row) => {
      if (
        hasGroups &&
        !groups.some((group) => row.groupIds.includes(group.id))
      ) {
        return false;
      }
      if (hasYears && !years.includes(row.yearLevel)) {
        return false;
      }
      if (hasRisks && !risks.some((risk) => row.risks.includes(risk))) {
        return false;
      }
      if (!this.includeStudentsWithNoMedical && !row.hasMedicalInfo) {
        return false;
      }

      return true;
    });
  }

  @computed('selected.[]', 'filteredLearners.@each.person')
  get filteredSelectedPeople() {
    let filteredPersons = mapBy(this.filteredLearners, 'person');
    return this.selected.filter((person) => filteredPersons.includes(person));
  }

  @computed(
    'filteredLearners.[]',
    'filteredLearners.@each.{fullName,planDisplay,planUploaded}',
    'fullNameFilter',
    'planFilter',
  )
  get tableFilteredLearners() {
    let nameFilter = (this.fullNameFilter || '').trim();
    let planFilter = (this.planFilter || '').trim();
    return this.filteredLearners.filter((row) => {
      if (!isEmpty(nameFilter)) {
        if (!filterValue(row.fullName || '', DataType.STRING, nameFilter)) {
          return false;
        }
      }
      if (!isEmpty(planFilter)) {
        if (!filterValue(row.planDisplay || '', DataType.STRING, planFilter)) {
          return false;
        }
      }
      return true;
    });
  }

  learnerRisks(person) {
    let risks = [];
    this.getCurrentUserConditions(
      person,
      this.includeNonCurrentConditions,
    ).forEach((condition) => {
      if (condition.anaphylaxis) {
        risks.push('Anaphylaxis');
      }
      if (condition.highRisk) {
        risks.push('High Risk');
      }
    });
    return risks;
  }

  @computed('model.groups.[]')
  get filteredGroups() {
    return this.model.groups.filter((group) => {
      if (group.type === GroupType.GROUP) {
        // Omit My Groups which the current user isn't a current staff member in.
        let staff = group.get('currentStaff') ?? [];
        return !!findBy(staff, 'personId', this.session.personId);
      }
      if (group.status === NSWStatus.INACTIVE) {
        return false;
      }
      // Include all other groups.
      return true;
    });
  }

  @setDiff('filteredGroups', 'groupFilter')
  remainingGroups;

  @computed('remainingGroups.[]')
  get sortedRemainingGroups() {
    let groupTypes = uniq(mapBy(this.remainingGroups, 'type')).sort();
    let sortedRemainingGroups = [];
    groupTypes.forEach((groupType) => {
      let semiFilteredGroups = filterBy(
        this.remainingGroups,
        'type',
        groupType,
      );
      let groupings = uniq(mapBy(semiFilteredGroups, 'grouping')).sort();
      groupings.forEach((grouping) => {
        sortedRemainingGroups.push(
          ...filterBy(semiFilteredGroups, 'grouping', grouping).sort(
            naturalSort('label'),
          ),
        );
      });
    });
    return sortedRemainingGroups;
  }

  clearSelection() {
    this.selected = [];
  }

  getCurrentUserConditions(person, includeNonCurrent) {
    let conditions = person.get('learner.medicalConditions');
    if (isEmpty(conditions)) {
      return [];
    }
    if (!includeNonCurrent) {
      conditions = filterBy(conditions, 'isCurrent');
    }
    return conditions.slice().sort((a, b) => a.priority - b.priority);
  }

  _buildLearnerRow(person) {
    let allConditions = this._getAllConditions(person);
    let allAccessibleConditions = filterBy(
      allConditions,
      'userCanAccess',
      true,
    );
    let seriousConditions = filterBy(
      this._getSeriousConditions(person),
      'userCanAccess',
      true,
    );
    let allergyConditions = filterBy(
      this._getNonSeriousAllergies(person),
      'userCanAccess',
      true,
    );
    let otherConditions = filterBy(
      this._getNonSeriousConditions(person),
      'userCanAccess',
      true,
    );

    let anyConditionHasTreatment = allAccessibleConditions.some(
      (condition) => condition.medications.length > 0,
    );

    let legacyCritical = person.get('fields.core:criticalInfo.value');
    let legacyAllergy = person.get('fields.core:allergies.value');
    let legacyMedicalCondition = person.get(
      'fields.core:medicalCondition.value',
    );
    let legacyMedicalTreatment = person.get(
      'fields.core:medicalTreatment.value',
    );
    let legacyDoctor = person.get('fields.core:doctor.value');

    let planUploaded = this._planUploaded(person, allConditions);

    let medicalContacts = filterBy(
      this._getMedicalContacts(person),
      'userCanAccess',
      true,
    );
    let medicationsWithoutConditions = this._getNonConditionMedications(person);

    let groupIds = mapBy(get(this.groupMembershipMap, person.id) ?? [], 'id');
    let risks = this.learnerRisks(person);

    let hasNewConditions =
      seriousConditions.length > 0 ||
      allergyConditions.length > 0 ||
      otherConditions.length > 0;

    let hasLegacyConditions =
      !isEmpty(legacyCritical) ||
      !isEmpty(legacyAllergy) ||
      !isEmpty(legacyMedicalCondition);

    let hasMedicalTreatment =
      medicationsWithoutConditions.length > 0 ||
      !isEmpty(legacyMedicalTreatment) ||
      anyConditionHasTreatment;

    return {
      person,
      firstName: person.firstName,
      lastName: person.lastName,
      fullName: person.fullName,
      homeroom: person.homeroom,
      currentYearLevel: person.currentYearLevel,
      numericCurrentYearLevel: person.numericCurrentYearLevel,
      planUploaded,
      planDisplay: planUploaded ? 'Yes' : '',
      allConditions: allAccessibleConditions,
      seriousConditions,
      allergyConditions,
      otherConditions,
      medicationsWithoutConditions,
      medicalContacts,
      legacyCritical,
      legacyAllergy,
      legacyMedicalCondition,
      legacyMedicalTreatment,
      legacyDoctor,
      groupIds,
      risks,
      yearLevel: '' + person.get('currentYearLevel'),
      hasMedicalInfo:
        hasNewConditions || hasLegacyConditions || hasMedicalTreatment,
    };
  }

  _getMedicalContacts(person) {
    let contacts = this.includeNonCurrentMedical
      ? person.get('learner.medicalContacts')
      : person.get('learner.currentMedicalContacts');
    if (isEmpty(contacts)) {
      return [];
    }
    return contacts.toArray ? contacts.toArray() : contacts.slice();
  }

  _getAllConditions(person) {
    return this.getCurrentUserConditions(
      person,
      this.includeNonCurrentConditions,
    );
  }

  _getSeriousConditions(person) {
    let conditions = person.get('learner.medicalConditions');
    if (isEmpty(conditions)) {
      return [];
    }
    let filtered = filterBy(conditions, 'isSerious');
    if (!this.includeNonCurrentConditions) {
      filtered = filterBy(filtered, 'isCurrent');
    }
    return filtered.slice().sort((a, b) => a.priority - b.priority);
  }

  _getNonSeriousAllergies(person) {
    let conditions = person.get('learner.medicalConditions');
    if (isEmpty(conditions)) {
      return [];
    }
    let filtered = conditions.filter((condition) => {
      return (
        condition.type === MedicalConditionType.NON_SERIOUS_ALLERGY &&
        !condition.isSerious
      );
    });
    if (!this.includeNonCurrentConditions) {
      filtered = filterBy(filtered, 'isCurrent');
    }
    return filtered.slice().sort((a, b) => a.priority - b.priority);
  }

  _getNonSeriousConditions(person) {
    let conditions = person.get('learner.medicalConditions');
    if (isEmpty(conditions)) {
      return [];
    }
    let filtered = conditions.filter((condition) => {
      return (
        (condition.type ===
          MedicalConditionType.NON_SERIOUS_MEDICAL_CONDITION ||
          condition.type ===
            MedicalConditionType.SHORT_TERM_MEDICAL_CONDITION) &&
        !condition.isSerious
      );
    });
    if (!this.includeNonCurrentConditions) {
      filtered = filterBy(filtered, 'isCurrent');
    }
    return filtered.slice().sort((a, b) => a.priority - b.priority);
  }

  _getNonConditionMedications(person) {
    if (isEmpty(person) || isEmpty(this.model.medications)) {
      return [];
    }
    return this.model.medications.filter((medication) => {
      return (
        (isEmpty(medication.medicalCondition) ||
          medication.medicalCondition === undefined) &&
        medication.deleted === false &&
        person.id === medication.person_id
      );
    });
  }

  _planUploaded(person, conditions) {
    if (person.get('fields.core:aSCIAPlan.value') === 'Yes') {
      return true;
    }
    if (isEmpty(conditions)) {
      return false;
    }
    if (this.hasAnaphylaxis) {
      return conditions.some((condition) => {
        let listLength = condition.get(
          'fields.core:actionPlans.valueList.length',
        );
        if (!listLength) {
          return false;
        }
        let content = condition.get(
          'fields.core:actionPlans.valueList.content',
        );
        return content && content.includes && content.includes('anaphylaxis');
      });
    }
    return conditions.some((condition) => {
      return condition.get('fields.core:actionPlans.valueList.length') > 0;
    });
  }

  @action
  selectYearLevel(year) {
    this.yearLevelFilter = [...this.yearLevelFilter, year];
  }

  @action
  removeYearLevel(year) {
    this.yearLevelFilter = this.yearLevelFilter.filter((y) => y !== year);
  }

  @action
  selectGroup(group) {
    this.groupFilter = [...this.groupFilter, group];
  }

  @action
  removeGroup(group) {
    this.groupFilter = this.groupFilter.filter((g) => g !== group);
  }

  @action
  selectRisk(risk) {
    this.riskFilter = [...this.riskFilter, risk];
  }

  @action
  removeRisk(risk) {
    this.riskFilter = this.riskFilter.filter((r) => r !== risk);
  }

  @action
  toggleSelected(person) {
    if (this.selected.includes(person)) {
      this.selected = this.selected.filter((p) => p !== person);
    } else {
      this.selected = [...this.selected, person];
    }
  }

  @action
  toggleAll() {
    let visibleRows = this.tableFilteredLearners || [];
    if (isEmpty(visibleRows)) {
      return;
    }

    let visiblePersons = mapBy(visibleRows, 'person');
    let selectedVisibleCount = visiblePersons.filter((person) =>
      this.selected.includes(person),
    ).length;

    if (selectedVisibleCount >= visiblePersons.length) {
      this.selected = this.selected.filter(
        (person) => !visiblePersons.includes(person),
      );
    } else {
      let toAdd = visiblePersons.filter(
        (person) => !this.selected.includes(person),
      );
      this.selected = [...this.selected, ...toAdd];
    }
  }
}
